import { z } from 'zod';

import { apiSuccess, apiList, parseAdminApiResponse } from '@services/apiSchemas';

import { adminApiFetch } from './apiService';

// ---- Schemas (mirror app/controllers/api/admin/v1/orders_controller.rb) ----

// `get_records` — table view (controller L41-69).
const OrderTableRowSchema = z
  .object({
    urgent: z.boolean(),
    is_held: z.boolean(),
    id: z.string(),
    name: z.string(),
    extra_name: z.string().nullable(),
    note: z.string().nullable(),
    total_quantity: z.number(),
    shipped_quantity: z.number(),
    sales_channel_name: z.string().nullable(),
    status: z.string(),
    receiver_name: z.string().nullable(),
    shipping_type: z.string().nullable(),
    shipments: z.array(
      z
        .object({
          id: z.string(),
          status: z.string(),
          tracking_number: z.string().nullable(),
        })
        .passthrough(),
    ),
    scheduled_shipping_date: z.string().nullable(),
    delivery_date: z.string().nullable(),
    created_at: z.string(),
    placed_at: z.string().nullable(),
    shipped_at: z.string().nullable(),
    expected_ship_date: z.string().nullable(),
    sla_status: z.string().nullable(),
  })
  .passthrough();

// `suggest_name` — { name }
const SuggestNameSchema = z.object({ name: z.string() }).passthrough();

// `duplicate` — { id, name }（鏡像 operator orders#duplicate 回傳）
const DuplicateSchema = z.object({ id: z.string(), name: z.string() }).passthrough();

// `validate_fulfillment_names` — controller L703-709
const ValidateFulfillmentNamesSchema = z
  .object({
    total: z.number(),
    matched: z.number(),
    missing: z.number(),
    missing_fulfillment_names: z.array(z.string()),
    valid: z.boolean(),
  })
  .passthrough();

// `videos` — { videos: [...] }
const OrderVideosSchema = z
  .object({
    videos: z.array(
      z
        .object({
          id: z.string(),
          parcel_id: z.string().nullable(),
          public_url: z.string().nullable(),
          created_at: z.string(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

// `recording_sessions` — admin diagnostics for chunked QC video uploads.
// Status enum mirrors RecordingSession#status (pending/finalizing/completed/failed/abandoned).
const RecordingSessionSchema = z
  .object({
    session_id: z.string(),
    fulfillment_id: z.string(),
    parcel_id: z.string().nullable(),
    video_index: z.number().nullable(),
    status: z.string(),
    total_chunks: z.number().nullable(),
    gcs_chunk_count: z.number(),
    fulfillment_video_id: z.string().nullable(),
    failure_reason: z.string().nullable(),
    last_chunk_received_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

const OrderRecordingSessionsSchema = z
  .object({ sessions: z.array(RecordingSessionSchema) })
  .passthrough();

// force_finalize enqueues a BaseProgressWorker; the response carries the
// Sidekiq job_id so the FE can register it with addJob and follow progress
// via the BackgroundJobManager / NotificationChannel.
const ForceFinalizeResultSchema = z
  .object({
    job_id: z.string(),
  })
  .passthrough();

// `create` / `update` / `finalize_draft` etc — render `{id}` on success.
const OrderIdSchema = z.object({ id: z.string() }).passthrough();

// `show` — order_detail_payload is a large composite (preloads merchant,
// sales_channel, warehouse, ec_account, order_items, fulfillments, histories,
// shipments, serial numbers, etc.). The exact shape varies by branch and is
// re-shaped frequently — typing it here would lock callers into a snapshot
// and require lockstep updates with each backend tweak. Leave loose; consumer
// code already navigates the payload defensively.
const OrderDetailSchema = z.unknown();

// `integration_logs` / `integration_log_slice` — partner-specific, varies per
// source (third_party / partner / webhook).
const IntegrationLogsSchema = z.object({ logs: z.array(z.unknown()) }).passthrough();
const IntegrationLogSliceSchema = z.unknown();

const IdsListSchema = z.array(z.string());
const JobIdSchema = z.object({ job_id: z.string() }).passthrough();
const ImportJobSchema = z
  .object({ job_id: z.string(), import_id: z.string() })
  .passthrough();
// Generic envelope payload for endpoints whose body shape isn't load-bearing
// (single-action endpoints like cancel/resume, batch_* job kicks where
// callers only check `success`, getSerialNumbers etc).
const LooseDataSchema = z.unknown();

// ---- Type exports ---------------------------------------------------------

export type OrderTableRow = z.infer<typeof OrderTableRowSchema>;
export type OrderSuggestName = z.infer<typeof SuggestNameSchema>;
export type ValidateFulfillmentNamesResult = z.infer<typeof ValidateFulfillmentNamesSchema>;
export type OrderVideos = z.infer<typeof OrderVideosSchema>;
export type RecordingSessionRow = z.infer<typeof RecordingSessionSchema>;
export type OrderRecordingSessions = z.infer<typeof OrderRecordingSessionsSchema>;
export type ForceFinalizeResult = z.infer<typeof ForceFinalizeResultSchema>;

// `checkOrderNameAvailability` returns this adapted shape (NOT the raw
// envelope) — see method body.
export interface CheckOrderNameAvailabilityResult {
  exists: boolean;
  orderId: string | null;
  error: Error | null;
}

// ---- Service --------------------------------------------------------------

export const orderService = {
  // 列表
  getRecords: async (params: Record<string, unknown>) => {
    const response = await adminApiFetch('orders/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return apiList(OrderTableRowSchema).parse(await response.json());
  },

  // 全選 ID
  getIds: async (params: Record<string, unknown>) => {
    const response = await adminApiFetch('orders/get_ids', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return apiSuccess(IdsListSchema).parse(await response.json());
  },

  // 獲取單個訂單
  get: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}`, {
      method: 'GET',
    });
    return apiSuccess(OrderDetailSchema).parse(await response.json());
  },

  // 串接紀錄 tab — 列表（3 來源合併、metadata only）
  getIntegrationLogs: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/integration_logs`, {
      method: 'GET',
    });
    return apiSuccess(IntegrationLogsSchema).parse(await response.json());
  },

  // 串接紀錄 tab — 點開某筆 row 取切片（third_party 走 pointer，partner / webhook 整筆 payload）
  getIntegrationLogSlice: async (id: string, source: string, logId: string) => {
    const response = await adminApiFetch(
      `orders/${id}/integration_logs/${source}/${logId}`,
      { method: 'GET' },
    );
    return apiSuccess(IntegrationLogSliceSchema).parse(await response.json());
  },

  // 建議訂單單號
  suggestName: async () => {
    const response = await adminApiFetch('orders/suggest_name', {
      method: 'GET',
    });
    return apiSuccess(SuggestNameSchema).parse(await response.json());
  },

  // 檢查庫存
  batchCheck: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_check', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 恢復訂單
  batchResume: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_resume', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 取消訂單
  batchCancel: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_cancel', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(JobIdSchema).parse(await response.json());
  },

  // 批次攔截訂單
  batchHold: async (ids: string[], reason: string, note?: string) => {
    const response = await adminApiFetch('orders/batch_hold', {
      method: 'POST',
      body: JSON.stringify({ ids, reason, note }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 匯出
  export: async (ids: string[], params: Record<string, unknown> = {}) => {
    const response = await adminApiFetch('orders/export', {
      method: 'POST',
      body: { ids, ...params },
    });
    return apiSuccess(JobIdSchema).parse(await response.json());
  },

  // 下載 PDF
  downloadPdf: async (ids: string[]) => {
    const response = await adminApiFetch('orders/download_pdf', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 建立退貨單
  batchCreateReturnOrder: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_create_return_order', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 草稿轉正式
  batchDraftOrderConvert: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_draft_order_convert', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 正式轉草稿
  batchConvertToDraftOrder: async (ids: string[], params: Record<string, unknown> = {}) => {
    const response = await adminApiFetch('orders/batch_convert_to_draft_order', {
      method: 'POST',
      body: JSON.stringify({ ids, ...params }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },


  // 正式轉急件
  batchConvertToUrgentOrder: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_convert_to_urgent_order', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 急件轉正式
  batchUrgentOrderConvert: async (ids: string[]) => {
    const response = await adminApiFetch('orders/batch_urgent_order_convert', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 批量更新 (Edit Modal)
  batchUpdate: async (ids: string[], actions: Record<string, unknown>) => {
    const response = await adminApiFetch('orders/batch_update', {
      method: 'POST',
      body: JSON.stringify({ ids, actions }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 批次拆分 fulfillment
  batchSplitFulfillments: async (ids: string[], params: Record<string, unknown>) => {
    const response = await adminApiFetch('orders/batch_split_fulfillments', {
      method: 'POST',
      body: JSON.stringify({ ids, ...params }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 移除訂單商品
  batchRemoveOrderProducts: async (ids: string[], params: Record<string, unknown>) => {
    const response = await adminApiFetch('orders/batch_remove_order_products', {
      method: 'POST',
      body: JSON.stringify({ ids, ...params }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 取消單個訂單
  cancel: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/cancel`, {
      method: 'POST',
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 恢復單個訂單
  resume: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/resume`, {
      method: 'POST',
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 重新檢查庫存
  recheckStock: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/recheck_stock`, {
      method: 'POST',
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 延遲單個訂單
  postpone: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/postpone`, {
      method: 'POST',
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 轉換為正常訂單
  convertToNormal: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/finalize_draft`, {
      method: 'POST',
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 釋放攔截
  releaseHolds: async (id: string, resolutionNote: string) => {
    const response = await adminApiFetch(`orders/${id}/release_holds`, {
      method: 'POST',
      body: JSON.stringify({ resolution_note: resolutionNote }),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 攔截訂單
  hold: async (id: string, params: Record<string, unknown> = {}) => {
    const response = await adminApiFetch(`orders/${id}/hold`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 生成 PDF
  generatePdf: async (id: string) => {
    const response = await adminApiFetch(`orders/${id}/generate_pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: null,
      }),
    });
    return apiSuccess(JobIdSchema).parse(await response.json());
  },

  // 獲取訂單影片
  getVideos: async (orderId: string) => {
    const response = await adminApiFetch(`orders/${orderId}/videos`, {
      method: 'GET',
    });
    return apiSuccess(OrderVideosSchema).parse(await response.json());
  },

  // 取得錄影 session 診斷資訊（含 GCS chunk 數量）— 後台救援工具用
  getRecordingSessions: async (orderId: string) => {
    const response = await adminApiFetch(`orders/${orderId}/recording_sessions`, {
      method: 'GET',
    });
    return apiSuccess(OrderRecordingSessionsSchema).parse(await response.json());
  },

  // 強制合併卡住的 RecordingSession（force=true，GCS prefix 掃描，跳過缺號 chunk）
  forceFinalizeRecordingSession: async (sessionId: string) => {
    const response = await adminApiFetch(`recording_sessions/${sessionId}/force_finalize`, {
      method: 'POST',
    });
    return apiSuccess(ForceFinalizeResultSchema).parse(await response.json());
  },

  // 檢查訂單名稱可用性 — 不走 apiSuccess 解析，因為 404 是預期的「不存在」
  // 路徑（不是錯誤），且呼叫端期待 { exists, orderId, error } 的 adapter shape。
  checkOrderNameAvailability: async (orderName: string): Promise<CheckOrderNameAvailabilityResult> => {
    try {
      const response = await adminApiFetch(`orders/0?name=${encodeURIComponent(orderName.trim())}`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.id) {
          return { exists: true, orderId: data.data.id, error: null };
        } else {
          return { exists: false, orderId: null, error: new Error('Unexpected success response for non-existent order.') };
        }
      } else {
        if (response.status === 404) {
          return { exists: false, orderId: null, error: null };
        } else {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            // response was not JSON
          }
          return { exists: false, orderId: null, error: new Error(errorMessage) };
        }
      }
    } catch (err) {
      return { exists: false, orderId: null, error: err instanceof Error ? err : new Error(String(err)) };
    }
  },

  // 創建訂單
  createOrder: async (payload: Record<string, unknown>) => {
    const response = await adminApiFetch('orders', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return apiSuccess(OrderIdSchema).parse(await response.json());
  },

  // 驗證出貨單名稱是否存在
  validateFulfillmentNames: async (fulfillmentNames: string[]) => {
    const response = await adminApiFetch('orders/validate_fulfillment_names', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fulfillment_names: fulfillmentNames }),
    });
    return apiSuccess(ValidateFulfillmentNamesSchema).parse(await response.json());
  },

  // 上傳物流標籤（以 Fulfillment 為主體）
  uploadLabel: async (formData: FormData) => {
    const response = await adminApiFetch('orders/upload_label', {
      method: 'POST',
      body: formData,
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 更新訂單

  // 更新訂單

  // 更新訂單
  update: async (id: string, payload: Record<string, unknown>) => {
    const response = await adminApiFetch(`orders/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return apiSuccess(OrderIdSchema).parse(await response.json());
  },

  // 修正訂單資訊（僅限地址、電話等欄位，不觸發重新分配）
  correct: async (id: string, payload: Record<string, unknown>) => {
    const response = await adminApiFetch(`orders/${id}/correct`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return apiSuccess(LooseDataSchema).parse(await response.json());
  },

  // 匯入訂單
  import: async (formData: FormData) => {
    const response = await adminApiFetch('orders/import', {
      method: 'POST',
      body: formData,
    });
    return apiSuccess(ImportJobSchema).parse(await response.json());
  },

  // 下載匯入模板
  downloadImportTemplate: (locale: string) => {
    return adminApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify({ type: 'order', locale }),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },

  // 序號列表
  getSerialNumbers: async (params: Record<string, unknown>) => {
    const response = await adminApiFetch('orders/serial_numbers/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return apiList(LooseDataSchema).parse(await response.json());
  },

  // 複製訂單（鏡像 operator orderService.duplicate；name 省略時後端預設 {原名}-COPY，撞名則回 422）。
  // 用 parseAdminApiResponse 而非 apiSuccess(...).parse：撞名時後端回 422 + order_name_already_exists，
  // 該 envelope 無 data 欄位，直接 .parse() 會丟 ZodError 被 catch 當成通用錯誤；
  // parseAdminApiResponse 會保留 error envelope，讓 OrderShow 依 message 顯示具體提示。
  duplicate: async (id: string, name?: string) => {
    const body = name ? { name } : {};
    const response = await adminApiFetch(`orders/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return parseAdminApiResponse(response, apiSuccess(DuplicateSchema));
  },

  // 上傳訂單附件（FormData，沿用 purchaseOrderService 慣例）
  createAttachment: async (orderId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await adminApiFetch(`orders/${orderId}/order_attachments`, {
      method: 'POST',
      body: formData,
    });
    return response.json();
  },

  // 刪除訂單附件（僅手動上傳的附件可刪；匯入批次附件唯讀）
  deleteAttachment: async (orderId: string, attachmentId: string) => {
    const response = await adminApiFetch(`orders/${orderId}/order_attachments/${attachmentId}`, {
      method: 'DELETE',
    });
    return response.json();
  },
};

export default orderService;
